import React, { useEffect, useRef, useState } from "react";
import ProgressTracker from "../../../../../utils/ProgressTracker";
import jsPDF from "jspdf";
import { enqueueSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { salesProjectcompleteFinal, rejectManagerProjectCompletion } from "./../../../../../store/slices/ProjectOngoing/projectOngoingSlice";
import { fetchAllProjects } from "../../../../../store/slices/projectsSlice";
import CheckIcon from "@mui/icons-material/Check";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import CommaConvert from "../../../../../utils/CommaConvert";
import {
  formatAmountComma,
  parseAmountNumber,
} from "../../../../../utils/decimalAmountUtils";
import DateConversion from "../../../../../utils/DateConversion";
import {
  buildProjectDescriptionAliasIds,
  descriptionMatchesProjectAliases,
  descriptionTaskOwnerId,
  mergeProjectDescriptionRowsForAssignUI,
} from "../../../../../utils/projectAssignDescriptionMerge";
import {
  consolidateTrainingExpenseRowsForDisplay,
  getLiveTrainingRollupForOpsAssigneeProject,
  isInstallationAssignmentRow,
  isTrainingExpenseDisplayRow,
  normalizeAssignmentTypeServiceKey,
  normalizeDescriptionForTrainingCostMatch,
  normalizeTaskIdForScope,
  taskTypeServiceMatchesAssignmentRow,
} from "../../../../../utils/trainingOtherExpenseSync";
import { calculateProjectOngoingProgress } from "../../../../../utils/calculateProjectOngoingProgress";

const normalizeOngoingExpenseLabel = (raw) =>
  normalizeDescriptionForTrainingCostMatch(raw);

const normalizeOwnerIdForMatch = (id) => {
  const fromEntry = descriptionTaskOwnerId({ taskownerid: id });
  const s = fromEntry || String(id ?? "").trim();
  return normalizeTaskIdForScope(s) || s;
};

const isOngoingDescriptionComplete = (item) =>
  item?.isTaskComplete === true ||
  item?.isTaskComplete === 1 ||
  item?.isTaskcomplete === true ||
  item?.isTaskcomplete === 1 ||
  String(item?.isTaskComplete ?? item?.isTaskcomplete ?? "")
    .toLowerCase()
    .trim() === "true";

const parseOngoingExpenseRaw = (item) => {
  if (!item) return null;
  const raw = item?.expenses ?? item?.Expenses ?? item?.expense;
  if (raw == null || raw === undefined) return null;
  if (String(raw).trim() === "") return null;
  const n = parseAmountNumber(raw);
  return Number.isFinite(n) ? n : null;
};

/** SP ongoing: show Expenses when ops saved expenses (not planned Amount/value). */
const hasFilledOngoingExpense = (item) => {
  if (isTrainingExpenseDisplayRow(item)) {
    return (
      isOngoingDescriptionComplete(item) &&
      Number(item?.value ?? item?.expenses ?? 0) > 0
    );
  }
  return parseOngoingExpenseRaw(item) != null;
};

const getFilledOngoingExpenseNumber = (item) => {
  if (!hasFilledOngoingExpense(item)) return null;
  if (isTrainingExpenseDisplayRow(item)) {
    const n = Number(item?.expenses ?? item?.value ?? 0);
    return Number.isFinite(n) ? n : 0;
  }
  return parseOngoingExpenseRaw(item) ?? 0;
};

const preferOngoingDescriptionRow = (a, b) => {
  const expA = parseOngoingExpenseRaw(a) ?? 0;
  const expB = parseOngoingExpenseRaw(b) ?? 0;
  if (expA !== expB) return expA > expB;
  if (isOngoingDescriptionComplete(a) !== isOngoingDescriptionComplete(b)) {
    return isOngoingDescriptionComplete(a);
  }
  return false;
};

/** Sales ongoing: dedupe by Mongo _id only — do not collapse distinct lines that share a label. */
const dedupeSalesOngoingDescriptionsById = (rows) => {
  const byId = new Map();
  const withoutId = [];
  for (const row of rows || []) {
    const id = String(row?._id ?? "").trim();
    if (!id) {
      withoutId.push(row);
      continue;
    }
    if (!byId.has(id)) byId.set(id, row);
  }
  return [...byId.values(), ...withoutId];
};

/** Collapse duplicate template/tracking rows per assignee+label; keep all distinct line items. */
const dedupeOngoingDescriptionsByLabel = (rows) => {
  const byAssigneeLabel = new Map();
  const byId = new Map();
  for (const row of rows || []) {
    const id = String(row?._id ?? "").trim();
    if (id) {
      if (!byId.has(id)) byId.set(id, row);
      continue;
    }
    const label = normalizeOngoingExpenseLabel(row?.description);
    if (!label) continue;
    const owner = normalizeOwnerIdForMatch(descriptionTaskOwnerId(row));
    const key = `${owner}::${label}`;
    const prev = byAssigneeLabel.get(key);
    if (!prev || preferOngoingDescriptionRow(row, prev)) {
      byAssigneeLabel.set(key, row);
    }
  }
  const merged = [...byId.values(), ...byAssigneeLabel.values()];
  const seenId = new Set();
  return merged.filter((row) => {
    const id = String(row?._id ?? "").trim();
    if (!id) return true;
    if (seenId.has(id)) return false;
    seenId.add(id);
    return true;
  });
};

const formatOngoingExpenseCell = (item, commaFormat) => {
  const n = getFilledOngoingExpenseNumber(item);
  if (n == null) return "";
  return commaFormat(n);
};

const sumFilledOngoingExpenses = (items) =>
  (items || []).reduce((sum, item) => {
    const n = getFilledOngoingExpenseNumber(item);
    return sum + (n != null ? n : 0);
  }, 0);

const formatOngoingExpenseTotal = (items, commaFormat) => {
  const list = items || [];
  if (!list.some((i) => hasFilledOngoingExpense(i))) return "";
  return commaFormat(sumFilledOngoingExpenses(list));
};

/**
 * Installation Other Expenses for L3 ongoing task list: keep rows with saved Amount (value)
 * or saved Expenses (expenses) — shared util only checks value, which hid Airfare etc. when
 * ops/manager saved expenses but left quoted Amount empty.
 */
const filterInstallationOngoingOtherExpenseDisplayForSalesOngoing = (
  rows,
  projectRow
) => {
  if (!isInstallationAssignmentRow(projectRow)) {
    return rows;
  }
  const list = Array.isArray(rows) ? rows : [];
  return list.filter((entry) => {
    if (!entry || typeof entry !== "object") return false;
    if (isTrainingExpenseDisplayRow(entry)) return true;
    if (entry._isTrainingCost || entry._prmSyntheticTrainingExpense) return true;
    const id = String(entry._id ?? "");
    if (
      id.startsWith("training_cost_") ||
      id.startsWith("prm-synthetic") ||
      id.startsWith("prm-training-rollup")
    ) {
      return true;
    }
    const valueRaw = entry?.value ?? entry?.Value ?? "";
    if (valueRaw != null && String(valueRaw).trim() !== "") {
      const n = parseAmountNumber(valueRaw);
      if (Number.isFinite(n) && n > 0) return true;
    }
    return hasFilledOngoingExpense(entry);
  });
};

const ShowTaskDetails = ({
  row,
  employees,
  projects,
  projectTrackingWorkEntries,
  descriptionProjectTracking,
  hardwareProjectTracking,
  entries,
  tasks,
  setIsLoading,
  showListState,
  setShowListState,
  setRowName,
  handlerefresh,
  selectedTaskId,
  autoExpandAllTaskDetails,
  onAutoExpandAllTaskDetailsConsumed,
  isViewOnly = false,
}) => {
  const dispatch = useDispatch();
  const currency = useCurrencySymbol();
  const USAComma = CommaConvert();
  const { formatDateByCountry } = DateConversion();
  const { mytrainings } = useSelector((state) => state.myTrainings);

  const [matchedRowProjectsAll, setMatchedRowProjectsAll] = useState([]);

  useEffect(() => {
    const matchedRowProject = projects.filter((pro) =>
      (row.quotationId != null && pro.quotationId != null)
        ? String(pro.quotationId) === String(row.quotationId)
        : pro.ProjectName === row.ProjectName
    );

    const filteredProjects = matchedRowProject.filter((project) => {
      // If Assignedperson is missing or empty, keep the project
      if (!project.Assignedperson || project.Assignedperson.length === 0) {
        return true;
      }

      // Check if ANY person has forbidden status
      return !project.Assignedperson.some(
        (person) =>
          person.salespersonstatus === "project assigned to other" ||
          person.operationstatus === "project assigned to other"
      );
    });

    setMatchedRowProjectsAll(filteredProjects);
  }, [projects, row]);

  const [matchedProjects, setMatchedProjects] = useState([]);

  useEffect(() => {
    if (projects?.length && row?.Projects?.length) {
      const matchedProjects = [];

      row.Projects.forEach((rowProject) => {
        const old = projects.find((pro) => {
          if (rowProject.quotationId != null && pro.quotationId != null) {
            return String(pro.quotationId) === String(rowProject.quotationId) && pro.TypeService === rowProject.TypeService;
          }
          return pro.TypeService === rowProject.TypeService && pro.ProjectName === rowProject.ProjectName;
        });

        if (old) {
          const isSame =
            old._id === rowProject.oldprojectid &&
            (old.quotationId != null && rowProject.quotationId != null ? String(old.quotationId) === String(rowProject.quotationId) : old.ProjectName === rowProject.ProjectName) &&
            old?.TypeService === rowProject?.TypeService;

          matchedProjects.push({
            _id: rowProject._id, // ✅ add row project id at top-level
            ProjectName: rowProject.ProjectName,
            TypeService: rowProject.TypeService, // ✅ add row type service
            oldProject: old,
            newProject: isSame ? old : rowProject, // replace with old if identical
          });
        }
      });
      setMatchedProjects(matchedProjects); // <-- store in your state
    }
  }, [projects, row?.Projects]);

  const [allrowData, setAllrowData] = useState([]);

  useEffect(() => {
    if (
      matchedProjects?.length &&
      (entries?.length ||
        tasks?.length ||
        projectTrackingWorkEntries?.length ||
        descriptionProjectTracking?.length)
    ) {
      const updated = matchedProjects.map((proj) => {
        // old matches
        const oldworkEntries = entries.filter(
          (entry) => entry.projectid === proj.oldProject._id
        );
        const oldAliasIds = buildProjectDescriptionAliasIds(
          proj.oldProject,
          projects
        );
        const oldDescriptions = tasks.filter((task) =>
          descriptionMatchesProjectAliases(task, oldAliasIds)
        );
        const newworkEntries = projectTrackingWorkEntries.filter(
          (entry) => entry.projectid === proj.newProject._id
        );
        const newDescriptions = mergeProjectDescriptionRowsForAssignUI({
          projectRow: proj.newProject,
          trackingRows: descriptionProjectTracking,
          persistedRows: tasks,
          projectsList: projects,
        });

        const newHardwarePart = hardwareProjectTracking.filter(
          (desc) => desc.projectid === proj.newProject._id
        );

        return {
          _id: proj.newProject._id,
          TypeService: proj.newProject.TypeService,
          ProjectName: proj.newProject.ProjectName,
          Assignedperson: proj.newProject.Assignedperson,
          oldProject: {
            _id: proj.oldProject._id,
            ProjectName: proj.oldProject.ProjectName,
            TypeService: proj.oldProject.TypeService,
            Assignedperson: proj.oldProject.Assignedperson,
            oldworkEntries,
            oldDescriptions,
          },
          newProject: {
            _id: proj.newProject._id,
            ProjectName: proj.newProject.ProjectName,
            TypeService: proj.newProject.TypeService,
            Assignedperson: proj.newProject.Assignedperson,
            newworkEntries,
            newDescriptions,
            newHardwarePart: newHardwarePart,
          },
        };
      });

      setAllrowData(updated); // ✅ update state
    }
  }, [
    matchedProjects,
    hardwareProjectTracking,
    entries,
    tasks,
    projectTrackingWorkEntries,
    descriptionProjectTracking,
    projects,
  ]);

  const [unifitedList, setUnifitedList] = useState([]);

  
  const processHardwareDescriptions = ({
    allDescriptions,
    employees,
    currentEmpId,
  }) => {
    const empMap = employees.reduce((acc, e) => {
      acc[e._id] = e;
      return acc;
    }, {});

    const ownerIdOf = (part) => normalizeOwnerIdForMatch(descriptionTaskOwnerId(part));
    const roleOf = (part) => {
      const oid = ownerIdOf(part);
      return empMap[oid] || empMap[part?.taskownerid];
    };

    const grouped = allDescriptions.reduce((acc, part) => {
      if (!acc[part.description]) acc[part.description] = [];
      acc[part.description].push(part);
      return acc;
    }, {});

    const result = [];
    const currentEmpKey = normalizeOwnerIdForMatch(currentEmpId);

    Object.values(grouped).forEach(parts => {
      const pmParts = parts.filter((p) =>
        roleOf(p)?.customRoles?.includes("project manager")
      );

      const opParts = parts.filter(
        (p) =>
          roleOf(p)?.customRoles?.includes("operations") &&
          !roleOf(p)?.customRoles?.includes("project manager")
      );

      const highestPM = pmParts.length
        ? pmParts.reduce((a, b) => (b.value > a.value ? b : a))
        : null;

      const highestOP = opParts.length
        ? opParts.reduce((a, b) => (b.value > a.value ? b : a))
        : null;

      // 🔹 Operations keep their own
      opParts.forEach((p) => {
        if (ownerIdOf(p) === currentEmpKey) {
          result.push(p);
        }
      });

      // 🔹 PM logic
      const isPM =
        empMap[currentEmpKey]?.customRoles?.includes("project manager");
      if (!isPM) return;

      if (!highestPM && highestOP) {
        result.push({ ...highestOP });
        return;
      }

      if (
        highestPM &&
        (!highestOP || highestPM.value >= highestOP.value)
      ) {
        if (ownerIdOf(highestPM) === currentEmpKey) {
          result.push(highestPM);
        }
        return;
      }

      if (highestPM && highestOP && highestOP.value > highestPM.value) {
        result.push({ ...highestOP });
      }
    });

    return dedupeOngoingDescriptionsByLabel(result);
  };

  useEffect(() => {
    if (allrowData?.length && employees?.length) {
      const mergedData = allrowData.map((project) => {
        const assignedData = project.newProject.Assignedperson.map((person) => {
          const emp =
            employees.find((e) => e.EmpEmail === person.email) ||
            employees.find((e) => {
              const full = `${e.EmpFirstName} ${e.EmpLastName}`
                .trim()
                .toLowerCase();
              return full === String(person?.name ?? "").trim().toLowerCase();
            });
          if (!emp) return null; // skip if no match
          const empid = emp._id;
          const projectAliasIds = buildProjectDescriptionAliasIds(
            project.newProject,
            projects
          );
          const personEmail = String(person?.email ?? emp?.EmpEmail ?? "")
            .trim()
            .toLowerCase();
          const personNameRaw = String(person?.name ?? "").trim().toLowerCase();
          const personBaseName = personNameRaw
            .replace(/\s*\([^)]*\)\s*$/, "")
            .trim();
          const empFullName = `${emp.EmpFirstName} ${emp.EmpLastName}`
            .trim()
            .toLowerCase();
          const ownerIds = new Set(
            [empid, person?.personid, person?._id, person?.id]
              .filter(Boolean)
              .map((id) => normalizeOwnerIdForMatch(id))
          );
          employees.forEach((e) => {
            const eEmail = String(e.EmpEmail ?? "").trim().toLowerCase();
            const eName = `${e.EmpFirstName} ${e.EmpLastName}`.trim().toLowerCase();
            const emailMatch = personEmail && eEmail && eEmail === personEmail;
            const nameMatch =
              (empFullName && eName === empFullName) ||
              (personBaseName && eName === personBaseName) ||
              (personNameRaw && personNameRaw.includes(eName));
            if (emailMatch || nameMatch) {
              ownerIds.add(normalizeOwnerIdForMatch(e._id));
            }
          });
          const matchesOwner = (entry) => {
            const tid = normalizeOwnerIdForMatch(descriptionTaskOwnerId(entry));
            if (!tid) return false;
            if (ownerIds.has(tid)) return true;
            if (personEmail && tid.toLowerCase() === personEmail) return true;
            return false;
          };
          const matchesServiceLine = (entry) => {
            if (!descriptionMatchesProjectAliases(entry, projectAliasIds)) {
              return false;
            }
            if (taskTypeServiceMatchesAssignmentRow(entry, project.newProject)) {
              return true;
            }
            const taskKey = normalizeAssignmentTypeServiceKey(
              entry?.typeservice ?? entry?.TypeService ?? entry?.typeService
            );
            if (taskKey) return false;
            return taskTypeServiceMatchesAssignmentRow(
              { ...entry, typeservice: project.newProject?.TypeService },
              project.newProject
            );
          };
          // Ongoing bucket: use project-tracking descriptions only (not quoted-phase tasks).
          const allTrackingDescriptions =
            project.newProject?.newDescriptions || [];
          const assigneeQuotedLabels = new Set();
          (project.oldProject?.oldDescriptions || []).forEach((desc) => {
            if (!matchesServiceLine(desc)) return;
            if (!matchesOwner(desc)) return;
            const tid = normalizeOwnerIdForMatch(descriptionTaskOwnerId(desc));
            if (tid) ownerIds.add(tid);
            const lbl = normalizeOngoingExpenseLabel(desc?.description);
            if (lbl) assigneeQuotedLabels.add(lbl);
          });
          allTrackingDescriptions.forEach((desc) => {
            if (!matchesServiceLine(desc)) return;
            if (!matchesOwner(desc)) return;
            const tid = normalizeOwnerIdForMatch(descriptionTaskOwnerId(desc));
            if (tid) ownerIds.add(tid);
            const lbl = normalizeOngoingExpenseLabel(desc?.description);
            if (lbl) assigneeQuotedLabels.add(lbl);
          });
          const matchesAssigneeOtherExpenseLine = (entry) => {
            if (!matchesServiceLine(entry)) return false;
            if (matchesOwner(entry)) return true;
            const lbl = normalizeOngoingExpenseLabel(entry?.description);
            if (!lbl || !assigneeQuotedLabels.has(lbl)) return false;
            const tid = normalizeOwnerIdForMatch(descriptionTaskOwnerId(entry));
            return Boolean(tid && ownerIds.has(tid));
          };
          const role = emp.customRoles?.[0] || null;
          const isHardwareProjectManager =
            project.newProject?.TypeService === "Hardware" &&
            (emp.customRoles?.includes("project manager") ||
              role === "project manager");
          const name = `${emp.EmpFirstName} ${emp.EmpLastName}`;
          const oldworkEntries = project.oldProject.oldworkEntries.filter(
            (entry) =>
              matchesAssigneeOtherExpenseLine(entry) && matchesServiceLine(entry)
          );
          const newworkEntries = project.newProject.newworkEntries.filter(
            (entry) =>
              matchesAssigneeOtherExpenseLine(entry) && matchesServiceLine(entry)
          );
          const oldDescriptions = project.oldProject.oldDescriptions.filter(
            (desc) =>
              matchesAssigneeOtherExpenseLine(desc) && matchesServiceLine(desc)
          );
                let newDescriptions = [];

          const assigneeTrackingDescriptions = allTrackingDescriptions.filter(
            (desc) => matchesServiceLine(desc) && matchesOwner(desc)
          );
          const supersededQuotedIds = new Set(
            assigneeTrackingDescriptions
              .map((desc) => String(desc?.oldtaskid ?? "").trim())
              .filter(Boolean)
          );
          const trackingOwnerLabelKeys = new Set(
            assigneeTrackingDescriptions.map((desc) => {
              const owner = normalizeOwnerIdForMatch(descriptionTaskOwnerId(desc));
              const label = normalizeOngoingExpenseLabel(desc?.description);
              return `${owner}::${label}`;
            })
          );
          const assigneeQuotedDescriptions = (
            project.oldProject?.oldDescriptions || []
          ).filter((desc) => {
            if (!matchesServiceLine(desc) || !matchesOwner(desc)) return false;
            const qid = String(desc?._id ?? "").trim();
            if (qid && supersededQuotedIds.has(qid)) return false;
            const owner = normalizeOwnerIdForMatch(descriptionTaskOwnerId(desc));
            const label = normalizeOngoingExpenseLabel(desc?.description);
            if (label && trackingOwnerLabelKeys.has(`${owner}::${label}`)) {
              return false;
            }
            return true;
          });
          const assigneeDescriptions = dedupeSalesOngoingDescriptionsById([
            ...assigneeTrackingDescriptions,
            ...assigneeQuotedDescriptions,
          ]);
          if (isHardwareProjectManager) {
            newDescriptions = processHardwareDescriptions({
              allDescriptions: assigneeDescriptions,
              employees,
              currentEmpId: empid,
            });
          } else {
            newDescriptions = assigneeDescriptions;
          }

          // Trainings: only when My Trainings has a cost for this assignee on this service line.
          {
            const liveRollup = getLiveTrainingRollupForOpsAssigneeProject({
              projectRow: project?.newProject,
              assigneeEmp: emp,
              trainingsEnvelope: mytrainings,
              projects,
              currencySymbol: currency,
            });
            const hasTrainingAlready = (newDescriptions || []).some(
              (d) => isTrainingExpenseDisplayRow(d)
            );
            const liveTotal = Number(liveRollup?.amount ?? 0);
            if (!hasTrainingAlready && Number.isFinite(liveTotal) && liveTotal > 0) {
              newDescriptions = [
                ...newDescriptions,
                {
                  _id: `training_cost_${empid}`,
                  taskownerid: empid,
                  description: liveRollup.description || "Training Cost",
                  value: liveTotal,
                  notes: liveRollup?.notes || "",
                  isTaskComplete: true,
                },
              ];
            }
            newDescriptions = consolidateTrainingExpenseRowsForDisplay(newDescriptions);
          }
          const expenseFilterProjectRow = {
            TypeService:
              project.newProject?.TypeService ?? project.TypeService,
          };
          if (isInstallationAssignmentRow(expenseFilterProjectRow)) {
            newDescriptions = filterInstallationOngoingOtherExpenseDisplayForSalesOngoing(
              newDescriptions,
              expenseFilterProjectRow
            );
          }
          // const newDescriptions = project.newProject.newDescriptions.filter(
          //   (desc) => desc.taskownerid === empid
          // );
          const newHardwareParts = project.newProject.newHardwarePart.filter(
            (desc) =>
              matchesAssigneeOtherExpenseLine(desc) && matchesServiceLine(desc)
          );
          return {
            TypeService: project.TypeService,
            name,
            empid,
            role,
            oldworkEntries,
            newworkEntries,
            oldDescriptions,
            newDescriptions,
            newHardwareParts,
            matchedPerson: person, // ✅ add this line
          };
        })
          .filter(Boolean) // remove nulls (non-matching employees)
          .sort((a, b) =>
            a.role === "project manager"
              ? -1
              : b.role === "project manager"
              ? 1
              : 0
          );

        if (project.newProject?.TypeService === "Hardware") {

          // 1️⃣ Create employee map for quick lookup
          const empMap = assignedData.reduce((acc, e) => {
            acc[e.empid] = e;
            return acc;
          }, {});

          // 2️⃣ Collect ALL newHardwareParts
          const allNewHardwareParts = assignedData.flatMap(
            e => e.newHardwareParts || []
          );

          // 3️⃣ Group by assemblypartdescription
          const groupedByAssembly = allNewHardwareParts.reduce((acc, part) => {
            const key = part.assemblepartdescription;
            if (!acc[key]) acc[key] = [];
            acc[key].push(part);
            return acc;
          }, {});

          // 4️⃣ Reset processed hardware array
          assignedData.forEach(e => {
            e.hardwarePartsOngoing = [];
          });

          // 5️⃣ Find Project Manager
          const pmEmp = assignedData.find(
            e => e.role === "project manager"
          );

          // 6️⃣ Apply rules
          Object.entries(groupedByAssembly).forEach(([desc, parts]) => {
            if (!pmEmp) return;

            const pmParts = parts.filter(
              p => empMap[p.taskownerid]?.role === "project manager"
            );

            const opParts = parts.filter(
              p => empMap[p.taskownerid]?.role === "operations"
            );

            // ✅ Operations always keep their own parts
            opParts.forEach(part => {
              const opEmp = empMap[part.taskownerid];
              if (opEmp) opEmp.hardwarePartsOngoing.push(part);
            });

            // ✅ Decide PM entry
            if (pmParts.length && opParts.length) {

              const highestPM = pmParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              const highestOP = opParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push(
                Number(highestOP.quantity) > Number(highestPM.quantity)
                  ? { ...highestOP }
                  : { ...highestPM }
              );

            } else if (!pmParts.length && opParts.length) {
              // 🔥 OP only → clone to PM
              const highestOP = opParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push({ ...highestOP });

            } else if (pmParts.length) {

              const highestPM = pmParts.reduce((a, b) =>
                Number(b.quantity) > Number(a.quantity) ? b : a
              );

              pmEmp.hardwarePartsOngoing.push({ ...highestPM });
            }
          });

          // 7️⃣ OPTIONAL: replace original newHardwareParts with processed data
          assignedData.forEach(e => {
            e.newHardwareParts = e.hardwarePartsOngoing;
          });
        }


        return {
          ...project,
          AssignedPersonData: assignedData, // ✅ merged data per employee
        };
      });
      setUnifitedList(mergedData);
    }
  }, [allrowData, employees, mytrainings, projects, currency]);

  const [openProbabilityIndex, setOpenProbabilityIndex] = useState(null);
  const [, setShowActionToAssign] = useState(null);

  const dropdownRefsProbability = useRef([]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openProbabilityIndex !== null &&
        dropdownRefsProbability.current[openProbabilityIndex] &&
        !dropdownRefsProbability.current[openProbabilityIndex].contains(
          event.target
        )
      ) {
        setOpenProbabilityIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openProbabilityIndex]);

  const downloadPDFHardware = (newHardwareParts) => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "Sr. No",
          "Part Name",
          "Part Number",
          "Quantity",
          `Unit Price (${currency})`,
          `Total Price (${currency})`,
          "Start Date",
          "End Date",
        ],
      ],
      body: newHardwareParts?.map((LeadReport, index) => [
        index + 1,
        LeadReport.assemblepartdescription,
        LeadReport.partno,
        LeadReport.quantity,
        USAComma(LeadReport.unitprice),
        USAComma(LeadReport.totalprice),
        LeadReport.startdate ? formatDateByCountry(LeadReport.startdate) : "",
        LeadReport.enddate ? formatDateByCountry(LeadReport.enddate) : "",
      ]),
      styles: {
        fontSize: 7,
      },
      headStyles: {
        fontSize: 8,
      },
    });
    doc.save("Hardware PartList.pdf");
  };

  const downloadPDFHardwareDescriptions = (newDescriptions) => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "Sr. No",
          "Description",
          `Amount (${currency})`,
          `Expenses (${currency})`,
          "Start Date",
          "End Date",
          "Notes",
        ],
      ],
      body: newDescriptions?.map((LeadReport, index) => [
        index + 1,
        LeadReport.description,
        formatAmountComma(LeadReport.value),
        formatOngoingExpenseCell(LeadReport, formatAmountComma),
        LeadReport.startdate ? formatDateByCountry(LeadReport.startdate) : "",
        LeadReport.enddate ? formatDateByCountry(LeadReport.enddate) : "",
        LeadReport.notes,
      ]),
      styles: {
        fontSize: 7,
      },
      headStyles: {
        fontSize: 8,
      },
    });
    doc.save("Hardware OtherExpensesList.pdf");
  };

  const salesProjectcomplete = async (row) => {
    if (isViewOnly) return;
    if (!row) {
      enqueueSnackbar("Failed To Complete Project", { variant: "error" });
    }
    const quotationId = row.quotationId ?? row.Projects?.[0]?.quotationId ?? row.ProjectName;
    const payload = {
      quotationId,
    };
    setIsLoading(true);
    try {
      await dispatch(salesProjectcompleteFinal(payload));
      enqueueSnackbar("Project Completed Successfully", { variant: "success" });
      dispatch(fetchAllProjects());
    } catch {
      enqueueSnackbar("Failed To Complete Project", { variant: "error" });
    } finally {
      setIsLoading(false);
      setCurrentProject(null);
      setClickedIdPerson(null);
      setShowActionToAssign("");
      setRowName("");
      handlerefresh();
    }
  };

  const getManagerRejectKey = (projectId, empid) =>
    `${String(projectId || "")}::${String(empid || "")}`;

  const toggleManagerRejectSelection = (projectId, empid, email) => {
    const key = getManagerRejectKey(projectId, empid);
    setSelectedRejectManagers((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = { projectId, email };
      }
      return next;
    });
  };

  const exitRejectMode = () => {
    setRejectMode(false);
    setSelectedRejectManagers({});
    setRejectConfirm(false);
  };

  const salesRejectManagers = async () => {
    if (isViewOnly) return;
    const managers = Object.values(selectedRejectManagers).filter(
      (m) => m?.projectId
    );
    // One rejection per service-line project
    const uniqueByProject = [];
    const seen = new Set();
    for (const m of managers) {
      const id = String(m.projectId);
      if (seen.has(id)) continue;
      seen.add(id);
      uniqueByProject.push(m);
    }
    if (!uniqueByProject.length) {
      enqueueSnackbar("Please select at least one manager to reject", {
        variant: "warning",
      });
      return;
    }
    setIsLoading(true);
    try {
      await dispatch(
        rejectManagerProjectCompletion({ managers: uniqueByProject })
      ).unwrap();
      enqueueSnackbar("Selected manager completion(s) rejected", {
        variant: "success",
      });
      exitRejectMode();
      dispatch(fetchAllProjects());
      if (typeof handlerefresh === "function") {
        handlerefresh();
      }
    } catch (err) {
      enqueueSnackbar(
        err?.message || "Failed to reject manager completion",
        { variant: "error" }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const divRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (divRef.current && !divRef.current.contains(event.target)) {
        setShowActionToAssign(""); // 👈 clear your state here
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [currentProject, setCurrentProject] = useState({});
  const [clickedIdPerson, setClickedIdPerson] = useState(null);
  const [expandedEmpIds, setExpandedEmpIds] = useState(() => new Set());

  useEffect(() => {
    if (selectedTaskId) {
      setClickedIdPerson(selectedTaskId?.taskownerid);
    }
  }, [selectedTaskId]);

  // When opened from Utilization Report: auto-expand Task Details for every person once.
  useEffect(() => {
    if (!autoExpandAllTaskDetails) return;
    if (!unifitedList?.length) return;
    const allIds = new Set();
    unifitedList.forEach((project) => {
      (project?.AssignedPersonData || []).forEach((p) => {
        if (p?.empid) allIds.add(p.empid);
      });
    });
    if (allIds.size > 0) {
      setExpandedEmpIds(allIds);
      setShowHideTitle(true);
      setShowListState("tasktable");
      if (typeof onAutoExpandAllTaskDetailsConsumed === "function") {
        onAutoExpandAllTaskDetailsConsumed();
      }
    }
  }, [
    autoExpandAllTaskDetails,
    unifitedList,
    onAutoExpandAllTaskDetailsConsumed,
    setShowListState,
  ]);

  useEffect(() => {
    if (!unifitedList?.length) return;
    if (selectedTaskId?.taskownerid) return;
    setCurrentProject(null);
    setClickedIdPerson(null);
  }, [unifitedList, selectedTaskId]);

  const calculateProgress = (project) => {
    const isProjectComplete = project?.Assignedperson?.every(
      (person) => person?.isProjectComplete
    );
    return calculateProjectOngoingProgress({
      workEntries: project?.newProject?.newworkEntries,
      descriptionEntries: project?.newProject?.newDescriptions,
      isProjectComplete: !!isProjectComplete,
    });
  };
  const calculateProgressHardware = (project) => {
    const isProjectComplete = project?.Assignedperson?.every(
      (person) => person?.isProjectComplete
    );
    return calculateProjectOngoingProgress({
      workEntries: project?.newProject?.newHardwarePart,
      descriptionEntries: project?.newProject?.newDescriptions,
      isProjectComplete: !!isProjectComplete,
    });
  };

  const [submitProject, setSubmitProject] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [selectedRejectManagers, setSelectedRejectManagers] = useState({});
  const [rejectConfirm, setRejectConfirm] = useState(false);

  const allManagersSubmitted = matchedRowProjectsAll.every((project) =>
    project.Assignedperson?.every((person) => person?.isProjectComplete)
  );
  const selectedRejectCount = Object.keys(selectedRejectManagers).length;

  const [showHideTitle, setShowHideTitle] = useState(true);
  useEffect(() => {
    if (!showHideTitle) {
      setShowListState("");
    } else {
      // setShowListState("tasktable")
    }
  }, [showHideTitle, setShowListState, showListState, clickedIdPerson]);

  console.log("unifitedList selectedTaskId", unifitedList);
  return (
    <>
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        className="flex flex-col w-full"
      >
        {unifitedList?.map((project, projectIndex) => (
          <div className=" mb-1">
            <div key={projectIndex} className="w-full flex border-b mb-1">
              <div className="w-[72%] flex flex-col min-w-0">
                {project.AssignedPersonData?.map((proj, index) => {
                  const isActive = selectedTaskId?.taskownerid === proj.empid;
                  const totalAssigned = (proj.newworkEntries || []).reduce(
                    (sum, item) => sum + (Number(item.hoursassigned) || 0),
                    0
                  );
                  const totalSpent = (proj.newworkEntries || []).reduce(
                    (sum, item) => sum + (Number(item.hoursspent) || 0),
                    0
                  );
                  const isOverSpent = totalSpent > totalAssigned;
                  const isExpanded = expandedEmpIds.has(proj.empid);
                  console.log("selectedTaskId isActive", isActive);
                  return (
                    <div key={index} className="w-full flex">
                      <div className="w-[30.5%] min-w-0 flex flex-col justify-center items-stretch border-b break-words [word-break:normal] hyphens-none whitespace-normal text-left">
                        <div
                          onClick={() => {
                            setCurrentProject(currentProject === proj ? null : proj);
                            setClickedIdPerson(clickedIdPerson === proj.empid ? null : proj.empid);
                            setExpandedEmpIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(proj.empid)) next.delete(proj.empid);
                              else next.add(proj.empid);
                              return next;
                            });
                            setShowHideTitle(true);
                            setShowListState("tasktable");
                          }}
                          className="w-full flex flex-col justify-center items-stretch break-words text-wrap text-left"
                        >
                          <div className="w-full flex rounded-md text-start">
                            <div className="w-full flex p-1 items-center">
                              {rejectMode &&
                                proj.role === "project manager" &&
                                proj.matchedPerson?.isProjectComplete && (
                                  <input
                                    type="checkbox"
                                    className="mr-1 cursor-pointer accent-red-600"
                                    checked={Boolean(
                                      selectedRejectManagers[
                                        getManagerRejectKey(project._id, proj.empid)
                                      ]
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      if (isViewOnly) return;
                                      toggleManagerRejectSelection(
                                        project._id,
                                        proj.empid,
                                        proj.matchedPerson?.email
                                      );
                                    }}
                                    disabled={isViewOnly}
                                    title="Select manager to reject"
                                  />
                                )}
                              <div
                                className={`${
                                  isOverSpent
                                    ? "text-red-600 font-semibold"
                                    : clickedIdPerson === proj.empid || isActive
                                    ? "font-bold"
                                    : ""
                                } w-[85%] min-w-0 rounded-md p-1 text-left`}
                              >
                                {proj.role === "project manager" ? (
                                  <>
                                    {proj.name}
                                    <br />
                                    <span className="inline-block w-full max-w-full shrink-0 whitespace-nowrap text-left [word-break:normal]">
                                      ({proj.TypeService}
                                      {"\u00a0"}
                                      Manager)
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="block w-full min-w-0 text-left break-words [word-break:normal] whitespace-normal">
                                      <span className="break-words [word-break:normal]">{proj.TypeService}</span>
                                      {" "}
                                      <span className="whitespace-nowrap shrink-0">{`Employee ${index}`}</span>
                                    </span>
                                  </>
                                )}
                              </div>
                              <div className="w-[15%] flex justify-start items-center">
                                {proj.role === "project manager" ? (
                                  <>
                                    {proj.matchedPerson.isProjectComplete ? (
                                      <span
                                        title="Completed"
                                        className="text-green-500 text-xl font-bold mr-1 rounded"
                                      >
                                        <CheckIcon />
                                      </span>
                                    ) : (
                                      <span
                                        title="Pending"
                                        className="text-orange-500 text-xl font-bold ml-2 rounded"
                                      >
                                        !
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {(() => {
                                      // Check if tasks have been submitted for ongoing (entries exist in tracking system)
                                      const hasWorkEntries = proj.newworkEntries && proj.newworkEntries.length > 0;
                                      const hasDescriptions = proj.newDescriptions && proj.newDescriptions.length > 0;
                                      const hasHardwareParts = proj.newHardwareParts && proj.newHardwareParts.length > 0;
                                      
                                      // Tasks must be submitted (exist in tracking) AND all must be complete
                                      const isSubmitted = hasWorkEntries || hasDescriptions || hasHardwareParts;
                                      
                                      if (!isSubmitted) {
                                        // Not submitted yet, show pending
                                        return (
                                          <span
                                            title="Pending"
                                            className="text-orange-500 text-xl font-bold ml-2 rounded"
                                          >
                                            !
                                          </span>
                                        );
                                      }
                                      
                                      // Check if all submitted tasks are complete
                                      const allWorkEntriesComplete = !hasWorkEntries || proj.newworkEntries.every(
                                        (item) => item.isTaskComplete
                                      );
                                      const allDescriptionsComplete = !hasDescriptions || proj.newDescriptions.every(
                                        (item) => item.isTaskComplete
                                      );
                                      const allHardwarePartsComplete = !hasHardwareParts || proj.newHardwareParts.every(
                                        (item) => item.isTaskComplete
                                      );
                                      
                                      const allComplete = allWorkEntriesComplete && allDescriptionsComplete && allHardwarePartsComplete;
                                      
                                      return allComplete ? (
                                        <span
                                          title="Completed"
                                          className="text-green-500 text-xl font-bold mr-1 rounded"
                                        >
                                          <CheckIcon />
                                        </span>
                                      ) : (
                                        <span
                                          title="Pending"
                                          className="text-orange-500 text-xl font-bold ml-2 rounded"
                                        >
                                          !
                                        </span>
                                      );
                                    })()}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        ref={divRef}
                        onClick={(e) => e.stopPropagation()}
                        className="w-[69.5%] min-w-0 border-b py-2 break-words text-wrap "
                      >
                        {isExpanded ? (
                          <>
                            {proj?.TypeService?.toLowerCase().includes(
                              "hardware"
                            )
                              ? showListState === "tasktable" && (
                                  <>
                                    <div
                                      onClick={(e) => {
                                        e.stopPropagation();
                                      }}
                                      className="w-full rounded-lg h-full justify-center"
                                    >
                                      <div
                                        ref={divRef}
                                        className="w-full border-b border-gray-300 overflow-hidden"
                                      >
                                        <div className="grid grid-cols-3 bg-[#e8e8e8] text-[11px]">
                                          <div className="p-1 border-l border-b  text-center">
                                            Total Parts{" "}
                                          </div>
                                          <div className="p-1 border-b text-center">
                                            Total Price ({currency})
                                          </div>
                                          <div className="p-1 border-r border-b text-center">
                                            Action
                                          </div>
                                        </div>

                                        <div className="grid grid-cols-3 bg-white text-black text-[11px]">
                                          <div className="p-1 border-l border-b  text-center">
                                            {proj?.newHardwareParts?.length}
                                          </div>
                                          <div className="p-1 border-b  text-center">
                                            {USAComma(
                                              proj?.newHardwareParts?.reduce(
                                                (sum, item) => {
                                                  return (
                                                    sum + (item.totalprice || 0)
                                                  );
                                                },
                                                0
                                              )
                                            )}{" "}
                                          </div>
                                          <div
                                            title="Download Hardware Part List"
                                            className="p-1 border-b border-r text-center cursor-pointer hover:bg-blue-50"
                                            onClick={() =>
                                              downloadPDFHardware(
                                                proj?.newHardwareParts
                                              )
                                            }
                                          >
                                            Download
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                )
                              : showListState === "tasktable" && (
                                  <div className="w-full flex  gap-2">
                                    <div className="w-full max-h-[14rem] overflow-auto">
                                      <div className="sticky top-0  text-[11px] text-center">
                                        <div className="flex bg-[#e8e8e8]">
                                          <div className="w-[30%] border-b border-l p-1">
                                            Task
                                          </div>
                                          <div className="w-[25%] border-b p-1">
                                            Hours Assigned
                                          </div>
                                          <div className="w-[25%] border-b p-1">
                                            Hours Spent
                                          </div>
                                          <div className="w-[20%] border-b border-r p-1">
                                            Notes
                                          </div>
                                        </div>
                                        <div className="flex font-bold">
                                          <div className="w-[30%] border-b border-l p-1">
                                            Total
                                          </div>
                                          <div className="w-[25%] border-b p-1">
                                            {proj.newworkEntries?.reduce(
                                              (sum, item) =>
                                                sum + item.hoursassigned,
                                              0
                                            ) || 0}
                                          </div>
                                          <div className="w-[25%] border-b p-1">
                                            {(
                                              proj.newworkEntries?.reduce(
                                                (sum, item) =>
                                                  sum +
                                                  (Number(item.hoursspent) || 0),
                                                0
                                              ) || 0
                                            ).toFixed(2)}
                                          </div>
                                          <div className="w-[20%] border-b border-r p-1"></div>
                                        </div>
                                      </div>

                                      {(proj.newworkEntries || []).map(
                                        (entry, index) => {
                                          const matchedOldWorkEntry =
                                            entries.find(
                                              (old) =>
                                                old._id === entry.oldtaskid
                                            );
                                          return (
                                            <div
                                              key={index}
                                              className="flex text-center text-[11px]"
                                            >
                                              <div
                                                className="w-[30%] border-b border-l p-1"
                                                title={
                                                  matchedOldWorkEntry
                                                    ? `Previous Name: ${matchedOldWorkEntry.task}`
                                                    : ""
                                                }
                                              >
                                                {entry.task}
                                              </div>
                                              <div
                                                className="w-[25%] border-b p-1"
                                                title={
                                                  matchedOldWorkEntry
                                                    ? `Previous Hrs: ${matchedOldWorkEntry.hoursassigned}`
                                                    : ""
                                                }
                                              >
                                                {entry.hoursassigned}
                                              </div>
                                              <div className="w-[25%] border-b p-1">
                                                {entry.hoursspent || ""}
                                              </div>
                                              <div
                                                className="w-[20%] border-b border-r p-1"
                                                title={
                                                  matchedOldWorkEntry
                                                    ? `Previous Notes: ${matchedOldWorkEntry.notes}`
                                                    : ""
                                                }
                                              >
                                                {entry.notes}
                                              </div>
                                            </div>
                                          );
                                        }
                                      )}
                                    </div>
                                  </div>
                                )}

                            {showListState === "otherexpenses" &&
                              (proj?.TypeService?.toLowerCase().includes(
                                "hardware"
                              ) ? (
                                // (proj.TypeService === "Hardware" ? (
                                <div
                                  ref={divRef}
                                  className="w-full border border-gray-300 overflow-hidden "
                                >
                                  <div className="grid grid-cols-4 bg-[#e8e8e8] text-[11px]">
                                    <div className="p-1 border-l border-b  text-center">
                                      Total Descriptions{" "}
                                    </div>
                                    <div className="p-1 border-b text-center">
                                      Amount ({currency})
                                    </div>
                                    <div className="p-1 border-b text-center">
                                      Expenses ({currency})
                                    </div>
                                    <div className="p-1 border-b border-r text-center">
                                      Action
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-4 bg-white text-black text-[11px]">
                                    <div className="p-1 border-b border-l  text-center">
                                      {proj?.newDescriptions?.length}
                                    </div>
                                    <div className="p-1 border-b  text-center">
                                      {formatAmountComma(
                                        proj?.newDescriptions?.reduce(
                                          (sum, item) => {
                                            return sum + parseAmountNumber(item.value);
                                          },
                                          0
                                        )
                                      )}{" "}
                                    </div>
                                    <div className="p-1 border-b  text-center">
                                      {formatOngoingExpenseTotal(
                                        proj?.newDescriptions,
                                        formatAmountComma
                                      )}{" "}
                                    </div>
                                    <div
                                      title="Download Description border-b border-r List"
                                      className="p-1 text-center cursor-pointer hover:bg-blue-50"
                                      onClick={() =>
                                        downloadPDFHardwareDescriptions(
                                          proj?.newDescriptions
                                        )
                                      }
                                    >
                                      Download
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full flex  gap-2">
                                  <div className="w-full max-h-[14rem] overflow-auto">
                                    <div className="sticky top-0  text-[11px] text-center">
                                      <div className="flex bg-[#e8e8e8]">
                                        <div className="w-[35%] border-b border-l p-1 truncate">
                                          Description
                                        </div>
                                        <div className="w-[20%] border-b p-1">
                                          Amount ({currency})
                                        </div>
                                        <div className="w-[20%] border-b p-1">
                                          Expenses ({currency})
                                        </div>
                                        <div className="w-[25%] border-b border-r p-1">
                                          Notes
                                        </div>
                                      </div>
                                      <div className="flex font-bold">
                                        <div className="w-[35%] border-b border-l p-1 truncate">
                                          Total
                                        </div>
                                        <div className="w-[20%] border-b p-1">
                                          {formatAmountComma(
                                            proj?.newDescriptions?.reduce(
                                              (sum, item) => sum + parseAmountNumber(item.value),
                                              0
                                            ) || 0
                                          )}
                                        </div>
                                        <div className="w-[20%] border-b p-1">
                                          {formatOngoingExpenseTotal(
                                            proj?.newDescriptions,
                                            formatAmountComma
                                          )}
                                        </div>
                                        <div className="w-[25%] border-b border-r p-1"></div>
                                      </div>
                                    </div>
                                    {proj.newDescriptions.map(
                                      (description, index) => {
                                        const matchedOldDescription =
                                          tasks.find(
                                            (old) =>
                                              old._id === description.oldtaskid
                                          );
                                        return (
                                          <div
                                            key={index}
                                            className="flex text-center text-[11px]"
                                          >
                                            <div
                                              className="w-[35%] border-b border-l p-1 max-h-14 overflow-auto"
                                              title={
                                                matchedOldDescription
                                                  ? `Previous Name: ${matchedOldDescription.description}`
                                                  : ""
                                              }
                                            >
                                              {description.description}
                                            </div>
                                            <div
                                              className="w-[20%] border-b p-1"
                                              title={
                                                matchedOldDescription
                                                  ? `Previous Value: ${matchedOldDescription.value}`
                                                  : ""
                                              }
                                            >
                                              {formatAmountComma(description.value)}
                                            </div>
                                            <div className="w-[20%] border-b p-1">
                                              {formatOngoingExpenseCell(
                                                description,
                                                formatAmountComma
                                              )}
                                            </div>
                                            <div
                                              className="w-[25%] border-b border-r p-1 max-h-14 overflow-auto"
                                              title={
                                                matchedOldDescription
                                                  ? `Previous Note: ${matchedOldDescription.notes}`
                                                  : ""
                                              }
                                            >
                                              {description.notes}
                                            </div>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                </div>
                              ))}
                          </>
                        ) : (
                          <div
                            onClick={() => {
                              setCurrentProject(
                                currentProject === proj ? null : proj
                              );
                              setClickedIdPerson(
                                clickedIdPerson === proj.empid
                                  ? null
                                  : proj.empid
                              );
                              setExpandedEmpIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(proj.empid)) next.delete(proj.empid);
                                else next.add(proj.empid);
                                return next;
                              });
                              // setShowListState("tasktable");
                              setShowHideTitle(true);
                              if (showListState === "tasktable") {
                                setShowListState("tasktable");
                              } else if (showListState === "otherexpenses") {
                                setShowListState("otherexpenses");
                              } else {
                                setShowListState("tasktable");
                              }
                            }}
                            className="flex justify-center items-center h-full w-full cursor-pointer"
                          >
                            Task Details
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="w-[28%] flex justify-center items-center px-3 pt-6">
                <div className="w-full max-w-[11rem]">
                  {project?.TypeService?.toLowerCase().includes("hardware") ? (
                    <ProgressTracker
                      value={calculateProgressHardware(project)}
                    />
                  ) : (
                    <ProgressTracker value={calculateProgress(project)} />
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="w-[100%] flex justify-end items-center gap-2 px-3 py-1">
          {allManagersSubmitted ? (
            <>
              {!isViewOnly && (
                <button
                  onClick={() => {
                    if (rejectMode) {
                      if (selectedRejectCount > 0) {
                        setRejectConfirm(true);
                      } else {
                        exitRejectMode();
                      }
                    } else {
                      setRejectMode(true);
                      setSelectedRejectManagers({});
                    }
                  }}
                  className={`py-1 px-2 rounded-md text-[11px] text-white ${
                    rejectMode && selectedRejectCount > 0
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-red-500 hover:bg-red-600"
                  }`}
                >
                  {rejectMode && selectedRejectCount > 0
                    ? `Reject Selected Manager${selectedRejectCount > 1 ? "s" : ""}`
                    : rejectMode
                    ? "Cancel Reject"
                    : "Reject Completion"}
                </button>
              )}
              <button
                onClick={() => {
                  if (!isViewOnly && !rejectMode) setSubmitProject(true);
                }}
                disabled={isViewOnly || rejectMode}
                className={`py-1 px-2 bg-blue-600 hover:bg-blue-800 text-white rounded-md text-[11px] ${
                  isViewOnly || rejectMode ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Approved Completion
              </button>
            </>
          ) : (
            <>
              <div className="py-1 px-2 inline-block text-orange-600 border rounded-md text-start bg-gray-100 text-[11px]">
                All Project Completion Pending from Manager
              </div>
              <button
                disabled
                className="py-1 px-2 bg-gray-400 text-white rounded-md text-[11px] opacity-70 cursor-not-allowed"
                title="Waiting for all managers to submit project"
              >
                Approved Completion
              </button>
            </>
          )}
        </div>

        {submitProject && (
          <div className="fixed inset-0 z-[3400] flex items-center justify-center bg-[rgba(0,0,0,0.4)] bg-opacity-50">
            <div className="bg-white rounded-lg p-4 max-w-2xl w-[80%] max-h-[90vh] overflow-auto relative">
              <button
                onClick={() => setSubmitProject(false)}
                className="absolute top-2 right-2 text-red-500 text-xl font-bold mr-6"
              >
                Close
              </button>
              <h2 className="text-[16px] text-start">
                Project Name :{" "}
                <span className="font-semibold">{row?.ProjectName}</span>
              </h2>

              <p className="mt-3 text-[14px] leading-6 text-start">
                Are you sure you want to approve this project ?
              </p>

              <div className="flex justify-start gap-4 mt-4">
                <button
                  onClick={() => {
                    if (isViewOnly) return;
                    salesProjectcomplete(row);
                    setSubmitProject(false);
                  }}
                  disabled={isViewOnly}
                  className={`py-2 px-7 bg-[#e32278] text-white rounded-lg ${isViewOnly ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  Yes
                </button>
                <button
                  onClick={() => {
                    setSubmitProject(false);
                  }}
                  className="py-2 px-7 bg-black hover:bg-white hover:text-black border border-black text-white rounded-lg"
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {rejectConfirm && (
          <div className="fixed inset-0 z-[3400] flex items-center justify-center bg-[rgba(0,0,0,0.4)] bg-opacity-50">
            <div className="bg-white rounded-lg p-4 max-w-2xl w-[80%] max-h-[90vh] overflow-auto relative">
              <button
                onClick={() => setRejectConfirm(false)}
                className="absolute top-2 right-2 text-red-500 text-xl font-bold mr-6"
              >
                Close
              </button>
              <h2 className="text-[16px] text-start">
                Project Name :{" "}
                <span className="font-semibold">{row?.ProjectName}</span>
              </h2>

              <p className="mt-3 text-[14px] leading-6 text-start">
                Are you sure you want to reject completion for the selected
                manager{selectedRejectCount > 1 ? "s" : ""}? They will need to
                update and submit the project again.
              </p>

              <div className="flex justify-start gap-4 mt-4">
                <button
                  onClick={() => {
                    if (isViewOnly) return;
                    salesRejectManagers();
                    setRejectConfirm(false);
                  }}
                  disabled={isViewOnly}
                  className={`py-2 px-7 bg-red-600 text-white rounded-lg ${
                    isViewOnly ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Yes, Reject
                </button>
                <button
                  onClick={() => setRejectConfirm(false)}
                  className="py-2 px-7 bg-black hover:bg-white hover:text-black border border-black text-white rounded-lg"
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ShowTaskDetails;
